import { Check, Quote } from "lucide-react";
import { PageShell } from "../components/layout/PageShell";
import { PageHero } from "../components/layout/PageHero";
import { CtaBand } from "../components/layout/CtaBand";
import { REVIEWS } from "../data/reviews";

const SITTING_REVIEWS = REVIEWS.filter((r) => ["alison", "christy", "hayley", "connie"].includes(r.id));

const HANDOVER = [
  "Feeding routines and quantities",
  "Walking requirements and equipment",
  "Sleeping arrangements",
  "Medication and health information",
  "Veterinary and emergency contacts",
  "House keys and alarm instructions",
  "Agreed access to areas of the home",
  "Household routines and property instructions",
  "How and when updates will be provided",
  "Exact arrival and departure times",
];

export default function PetSittingPage() {
  return (
    <PageShell title="In-home Pet Sitting">
      <PageHero
        testId="sitting-hero"
        eyebrow="In-home Pet Sitting"
        title="Your pets. Their home. Someone you trust."
        copy="Personal in-home care for holidays, work trips and time away, with routines agreed around the animals in your care."
        image="/images/marypawpins/lab-yarrow-dachshunds.webp"
        alt="A labrador resting contentedly among the wildflowers"
        imgPosition="object-[50%_35%]"
        ctaText="Enquire About Pet Sitting"
        ctaTo="/contact?service=In-home%20Pet%20Sitting"
      />

      <section className="py-14 sm:py-20" data-testid="sitting-why">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow">Why stay at home?</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-[#292A26] tracking-tight mt-3">
              Familiar surroundings, familiar routines.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#575953]">
              Many pets are more comfortable in their own surroundings, with familiar
              sleeping areas, feeding routines and the environment they know. In-home
              sitting means no kennels, no upheaval, plus the reassurance of a trusted
              person in your home for the agreed periods.
            </p>
            <p className="mt-4 text-base leading-relaxed text-[#575953]">
              Every booking is personally delivered by Carly. You know who will be
              staying in your property, caring for your animals and sending your updates.
            </p>
          </div>
          <div className="lg:col-span-6">
            <img
              src="/images/marypawpins/carly-cockapoo-selfie.jpg"
              alt="Carly relaxing with one of the dogs she cares for"
              className="rounded-3xl w-full aspect-[4/3] object-cover object-[50%_25%] shadow-[0_20px_60px_rgba(41,42,38,0.12)]"
            />
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-[#EFECE3]" data-testid="sitting-packages">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="eyebrow">Packages</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-medium text-[#292A26] tracking-tight mt-3">Two ways to book.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <article className="bg-white rounded-3xl border border-[#DDD7CB] p-8 lg:p-10" data-testid="silver-package-card">
              <p className="text-xs uppercase tracking-[0.22em] text-[#B9A58D] font-semibold">Silver</p>
              <p className="mt-3 font-serif text-4xl text-[#292A26]">£65 <span className="text-base text-[#575953] font-sans">per night</span></p>
              <p className="mt-4 text-sm leading-relaxed text-[#575953]">
                A 12-hour overnight stay in your home, keeping your pet's evening and
                morning routine exactly as they know it. Ideal for pets who are
                comfortable with their own company during the day.
              </p>
              <p className="mt-4 text-xs text-[#575953] italic">
                Arrival and departure times are agreed with Carly when you enquire, so
                the stay fits your pet's routine.
              </p>
            </article>
            <article className="bg-[#3F4A38] text-[#F7F4ED] rounded-3xl p-8 lg:p-10 relative overflow-hidden" data-testid="gold-package-card">
              <p className="text-xs uppercase tracking-[0.22em] text-[#D8B7B0] font-semibold">Gold</p>
              <p className="mt-3 font-serif text-4xl">£85 <span className="text-base text-[#F7F4ED]/70 font-sans">per 24 hours</span></p>
              <p className="mt-4 text-sm leading-relaxed text-[#F7F4ED]/80">
                The premium service: a 24-hour care period with a minimum of three
                walks a day, plus feeding, company and regular photo updates. Carly may
                be away for up to two hours. "24-hour care" means the booked care
                period, not uninterrupted supervision, and the routine is agreed with
                you before booking.
              </p>
            </article>
          </div>
          <div className="mt-8 rounded-2xl border border-[#DDD7CB] bg-white p-6 max-w-3xl" data-testid="sitting-group-walks-note">
            <h3 className="font-serif text-lg font-medium text-[#292A26]">Group walks during a sitting booking</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#575953]">
              Where suitable, and with your agreement, dogs staying with Carly can join
              her regular daily walks. Dogs who don't join must be comfortable at home
              during agreed absences. If your dog needs more continuous supervision,
              mention it when you enquire and Carly will discuss whether an alternative
              arrangement is possible.
            </p>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20" data-testid="sitting-handover">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="eyebrow">The home and the handover</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-[#292A26] tracking-tight mt-3">
              Everything agreed before you go.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#575953]">
              A premium sitting experience starts with a thorough handover. Every booking
              is discussed individually. Additional animals, travel requirements or
              extended arrival and departure times may affect the quote.
            </p>
            <p className="mt-4 text-base leading-relaxed text-[#575953]">
              Carly understands she is being trusted with your property as well as your
              pets. Your instructions are followed and your home is left in good order.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3.5">
              {HANDOVER.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-[#575953]">
                  <Check className="w-4 h-4 text-[#3F4A38] flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-[#EFECE3]" data-testid="sitting-reviews">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="eyebrow">Trusted with homes and pets</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-medium text-[#292A26] tracking-tight mt-3">In their own words.</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SITTING_REVIEWS.map((r) => (
              <figure key={r.id} className="bg-white rounded-3xl border border-[#DDD7CB] p-6 flex flex-col" data-testid={`sitting-review-${r.id}`}>
                <Quote className="w-5 h-5 text-[#D8B7B0]" />
                <blockquote className="mt-4 text-sm leading-relaxed text-[#292A26] flex-1">{r.text}</blockquote>
                <figcaption className="mt-5 pt-4 border-t border-[#E7E2D8]">
                  <p className="text-sm font-semibold text-[#292A26]">{r.attribution}</p>
                  <p className="text-xs text-[#575953]">{r.relation} · {r.source}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        testId="sitting-cta"
        heading="Planning time away?"
        copy="Tell Carly about your pets, your dates and the kind of care they need. She will be happy to discuss whether Mary Pawpins is the right fit."
        ctaTo="/contact?service=In-home%20Pet%20Sitting"
        buttonText="Enquire About Pet Sitting"
      />
    </PageShell>
  );
}
